import { getHashObj } from "@/utils/utils"
import responsesCache from "@/vue/planning/state/responsesCache"
import getCacheKey from "../../logic/getCacheKey"
import transportPackCode from "@/config/transportPackCode"
import flightPackCode from "@/config/flightPackCode"
import _ from "lodash"

type PlanningState = {
	// this is used by the green thing
	packCode: string
	seAvailabilities: { [key: string]: any } | null
	prices: { [key: string]: any } | null
	selectedPrestations: any
	selectedDates: any
	loading: boolean
	language: string
	cheapestPrestation: { [key: string]: any } | null
	cheapestPrice: number | null
	productUnavailable: boolean
	participants: Array<number>
	skipass: boolean
}

export default {
	refreshDataStoredOnWindow: (storeObj: any) => {
		const { state, getters } = storeObj

		const wiypData = getters.getWiypCallByFamilyCompositionAndPackageCodeAndId(`${state.participants.join('-')}-${state.flightOrigin}`, state.packCode, state.packageId);
		let cheapestPrice: number | null = null;
		if (wiypData && wiypData.wiypPrices) {
			const wiypPrices = wiypData.wiypPrices;
			cheapestPrice = wiypPrices.reduce((min: number, wiypPrice: any) => {
				return Math.min(min, wiypPrice.sellingPriceWiyp);
			}, Infinity);
		}

		const cacheKey = getCacheKey(
			state.packCode,
			state.dates.BeginDate,
			state.dates.EndDate,
			state.season,
			state.participants,
			state.skipass
		)
		const availResponse = responsesCache.value.getResponse(
			"availabilities",
			cacheKey
		)
		const priceResponse = responsesCache.value.getResponse("prices", cacheKey)

		const newPlanningState: PlanningState = {
			packCode: state.packCode,
			seAvailabilities: availResponse ? availResponse : null,
			prices: availResponse ? priceResponse : null,
			selectedPrestations: availResponse ? getters.selectedPrestations : null,
			selectedDates: state.dates,
			loading: state.loading,
			language: state.language,
			cheapestPrestation: availResponse
				? getters.prestations[getters.cheapestPrestationIndex]
				: null,
			cheapestPrice: cheapestPrice,
			productUnavailable: availResponse ? state.productUnavailable : null,
			participants: state.participants,
			skipass: state.skipass,
		}

		window.planningState = newPlanningState
		window.dispatchEvent(new Event("planningStateRefreshed"))		
	},

	ensurePrestationsPreselected: async (storeObj: any) => {
		const { state, getters, commit, dispatch } = storeObj

		const checkedNeedToPreselectPrestations =
			state.checkedNeedToPreselectPrestations
		const liheData = state.liheData

		if (checkedNeedToPreselectPrestations) return

		// retrieve selection from last time visited page during same session
		const selectedPrestIds_fromPreviousActivity_JSONstring =
			window.sessionStorage.getItem(`selectedPrestIds-${liheData.id}`)

		if (selectedPrestIds_fromPreviousActivity_JSONstring) {
			// preselect from previous activity (stored in sessionStorage)
			const selectedPrestIds_fromPreviousActivity = JSON.parse(
				selectedPrestIds_fromPreviousActivity_JSONstring
			)
			dispatch("setSelectedPrestIds", selectedPrestIds_fromPreviousActivity)
		} else {
			// preselect from hash if no previous activity recorded
			const hash = window.location.hash.replace("#", "")
			const hashObj: { [key: string]: any } = getHashObj(hash)
			const prest_id_source = hashObj.prest_id_source

			if (prest_id_source) {
				
				// Wait for variants to be loaded
				let attempts = 0;
				const maxAttempts = 10;
				
				while (attempts < maxAttempts) {
					const variants = getters.variants;
					
					if (variants && variants.length > 0) {
						// Find all variants that match this prest_id_source
						const matchingVariants = variants.filter(
							(variant: any) => variant.prestIdSource === prest_id_source
						);


						if (matchingVariants.length > 0) {
							// Select the first matching variant
							const variant = matchingVariants[0];
							
							const obj: { [key: string]: any } = {};
							obj[variant.id] = 1;
							obj["scrollToSummary"] = true;

							// If it's a skipass variant, also set the skipass duration
							if (variant.skipass) {
								const daysAmount = `days${variant.skipass}`;
								commit("setSkiPassDays", { daysAmount });
							}

							// Handle transport selection
							if (variant.packCode) {
								// Check if this is a transport pack using the proper config
								const isTransportPack = [...flightPackCode, ...transportPackCode].includes(variant.packCode);
								commit("setTransport", isTransportPack);
								
								// Set the package regardless of transport or not
								dispatch("setPackage", variant.packCode);
							}

							dispatch("setSelectedPrestIds", obj);
							break;
						}
						break;
					}
					
					// Wait 100ms before next attempt
					await new Promise(resolve => setTimeout(resolve, 100));
					attempts++;
				}
				
				if (attempts >= maxAttempts) {
					console.warn('[DEBUG] Timeout waiting for variants to load for prest_id_source:', prest_id_source);
				}
			}
		}
		commit("setCheckedNeedToPreselectPrestations", true)
	},

	toggleTransport: (storeObj: any) => {
		const { state, commit } = storeObj

		const newBool = !state.transportCheckbox
		commit("setTransport", newBool)
	},
}
